import React from 'react';

// Brand primary (see theme/brandTheme.js), inlined on purpose: this screen is
// the app's last line of defence, so it must not depend on modules that could
// themselves be part of the crash. Same reason the copy below is hardcoded
// bilingual instead of going through Localization.
const BRAND_PRIMARY = '#ad1179';

const MAX_DETAIL_LENGTH = 300;

// Stale-deploy detection: when a new build ships while a tab is still open,
// lazy chunks with old hashed URLs fail to load. A plain reload always fixes
// that, so it deserves a friendlier headline than a generic error.
function isStaleChunkError(error) {
  if (!error) {
    return false;
  }
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  return /loading (css )?chunk .+ failed/i.test(message);
}

// React can throw anything (Error, string, object, null); render a short,
// safe description without ever throwing from the fallback screen itself.
function describeError(error) {
  let text;
  try {
    if (error == null) {
      text = 'Unknown error';
    } else if (typeof error.message === 'string' && error.message.length > 0) {
      text = (error.name ? error.name + ': ' : '') + error.message;
    } else {
      text = String(error);
    }
  } catch (e) {
    text = 'Unknown error';
  }
  if (text.length > MAX_DETAIL_LENGTH) {
    text = text.slice(0, MAX_DETAIL_LENGTH) + '…';
  }
  return text;
}

// Last-resort error boundary around the whole app. Without one, any uncaught
// render/commit error makes React 18 unmount the entire tree and the user is
// left staring at a silent white page.
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, info) {
    console.error(
      '[AppErrorBoundary] Uncaught application error:',
      error,
      info && info.componentStack ? info.componentStack : info
    );
  }

  handleReload = () => {
    window.location.reload();
  };

  // Escape hatch for reload loops: if the crash comes from restored session
  // state, plain reload reproduces it forever. Dropping the saved sign-in
  // restarts from the login page instead.
  handleSignOutAndReload = () => {
    try {
      localStorage.removeItem('userdata');
    } catch (e) {
      // Storage unavailable (private mode / blocked) — reload is still the
      // best remaining option.
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const staleChunk = isStaleChunkError(this.state.error);
      return (
        <div style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '24px',
          fontFamily: 'CeraPro, "Noto Sans TC", Roboto, "Helvetica Neue", sans-serif',
          color: 'rgba(0, 0, 0, 0.7)',
        }}>
          {staleChunk ? (
            <div>
              <p style={{ fontSize: '18px', margin: '4px' }}>應用程式已更新，請重新載入頁面。</p>
              <p style={{ fontSize: '18px', margin: '4px' }}>The app has been updated. Please reload the page.</p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '18px', margin: '4px' }}>頁面發生錯誤，請重新載入頁面。</p>
              <p style={{ fontSize: '18px', margin: '4px' }}>Something went wrong. Please reload the page.</p>
            </div>
          )}
          <code style={{
            display: 'block',
            maxWidth: '640px',
            margin: '16px 0 0',
            padding: '8px 12px',
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 0.45)',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '6px',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}>
            {describeError(this.state.error)}
          </code>
          <button
            onClick={this.handleReload}
            style={{
              marginTop: '20px',
              padding: '10px 28px',
              fontSize: '16px',
              color: '#ffffff',
              backgroundColor: BRAND_PRIMARY,
              border: 'none',
              borderRadius: '24px',
              cursor: 'pointer',
            }}
          >
            重新載入 Reload
          </button>
          {!staleChunk && (
            <div>
              <p style={{ fontSize: '13px', margin: '20px 0 0', color: 'rgba(0, 0, 0, 0.5)' }}>
                如問題持續發生，請登出後再試。 If the problem keeps happening, sign out and try again.
              </p>
              <button
                onClick={this.handleSignOutAndReload}
                style={{
                  marginTop: '6px',
                  padding: '4px 8px',
                  fontSize: '13px',
                  color: BRAND_PRIMARY,
                  backgroundColor: 'transparent',
                  border: 'none',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                登出並重新載入 Sign out &amp; reload
              </button>
            </div>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
